import { Box, Grid, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { FleetContext } from "../../shared/components/page_base";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  LineChart,
  Line,
} from "recharts";

function MultiDayGraphList(props) {
  const { data, displayedRobots, fromDate, toDate } = props;
  const { robotStates } = useContext(FleetContext);

  const [hoursTicks, setHoursTicks] = useState([]);
  const [graphHeight, setGraphHeight] = useState(500);
  const [graphXs, setGraphXs] = useState(6);

  /// Run time graph data
  const [runTimeStatData, setRunTimeStatData] = useState([]);
  const [robotsRunTimeTotal, setRobotsRunTimeTotal] = useState(0);
  const [averageRunTimeHours, setAverageRunTimeHours] = useState(0);

  /// Idle time graph data
  const [idleTimeStatData, setIdleTimeStatData] = useState([]);
  const [robotsIdleTimeTotal, setRobotsIdleTimeTotal] = useState(0);
  const [averageIdleTimeHours, setAverageIdleTimeHours] = useState(0);

  /// Incidents graph data
  const [incidentStatData, setIncidentStatData] = useState([]);
  const [robotsIncidentTotal, setRobotsIncidentTotal] = useState(0);
  const [averageIncident, setAverageIncident] = useState(0);

  /// Tasks performed graph data (done + cancelled)
  const [taskStatData, setTaskStatData] = useState([]);
  const [robotsTaskTotal, setRobotsTaskTotal] = useState(0);
  const [averageTask, setAverageTask] = useState(0);

  /// Distance travelled graph data
  const [distanceStatData, setDistanceStatData] = useState([]);
  const [robotsDistanceTotal, setRobotsDistanceTotal] = useState(0);
  const [averageDistance, setAverageDistance] = useState(0);

  /// Area cleaned graph data
  const [areaCleanedStatData, setAreaCleanedStatData] = useState([]);
  const [robotsAreaCleanedTotal, setRobotsAreaCleanedTotal] = useState(0);
  const [averageAreaCleaned, setAverageAreaCleaned] = useState(0);

  const getDaysArray = function (start, end) {
    const arr = [];
    for (
      const dt = new Date(start);
      dt <= new Date(end);
      dt.setDate(dt.getDate() + 1)
    ) {
      arr.push(new Date(dt));
    }
    return arr;
  };

  const sameDay = function (d1, d2) {
    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    );
  };

  const secToStr = function (value) {
    const seconds = Math.floor(value % 60);
    const minutes = Math.floor((value / 60) % 60);
    const hours = Math.floor(value / 3600);

    return (
      (hours < 10 ? "0" + hours : hours) +
      ":" +
      (minutes < 10 ? "0" + minutes : minutes) +
      ":" +
      (seconds < 10 ? "0" + seconds : seconds)
    );
  };

  const getDateStr = function (d1) {
    return (
      d1.toLocaleString("en-US", {
        month: "long",
        day: "numeric",
      }) +
      ", " +
      d1.toLocaleString("en-US", {
        year: "numeric",
      })
    );
  };

  const get2DateStr = function (d1, d2) {
    return (
      " " +
      d1.toLocaleString("en-US", {
        month: "long",
        day: "numeric",
      }) +
      (d2 === null || d2.getFullYear() !== d1.getFullYear()
        ? ", " +
          d1.toLocaleString("en-US", {
            year: "numeric",
          })
        : "") +
      (d2 !== null
        ? " - " +
          d2.toLocaleString("en-US", {
            month: "long",
            day: "numeric",
          }) +
          ", " +
          d2.toLocaleString("en-US", {
            year: "numeric",
          })
        : "")
    );
  };

  useEffect(() => {
    if (
      !data ||
      !robotStates ||
      robotStates.size === 0 ||
      !displayedRobots ||
      displayedRobots.size === 0
    ) {
      return;
    }

    let maxHours = 0;

    let tmpRunTimeStatData = [];
    let tmpTotalRunTime = 0;

    let tmpIdleTimeStatData = [];
    let tmpTotalIdleTime = 0;

    let tmpIncidentStatData = [];
    let tmpTotalIncident = 0;

    let tmpTaskStatData = [];
    let tmpTotalTask = 0;

    let tmpDistanceStatData = [];
    let tmpTotalDistance = 0;

    let tmpAreaCleanedStatData = [];
    let tmpTotalAreaCleaned = 0;

    let robotSet = new Set();

    setGraphHeight(
      Math.max(500, 200 + Object.keys(displayedRobots).length * 100)
    );

    const daysList = getDaysArray(fromDate, toDate);

    if (daysList.length > 15) {
      setGraphXs(12);
    }

    for (let i = 0; i < daysList.length; i++) {
      tmpRunTimeStatData.push({
        name: daysList[i],
      });
      tmpIdleTimeStatData.push({
        name: daysList[i],
      });
      tmpIncidentStatData.push({
        name: daysList[i],
      });
      tmpTaskStatData.push({
        name: daysList[i],
      });
      tmpDistanceStatData.push({
        name: daysList[i],
      });
      tmpAreaCleanedStatData.push({
        name: daysList[i],
      });

      /// Setup data
      for (let [robot_id, robot] of displayedRobots) {
        if (robot.show) {
          robotSet.add(robot_id);
          tmpRunTimeStatData[i][robotStates.get(robot_id).name] = 0;
          tmpIdleTimeStatData[i][robotStates.get(robot_id).name] = 0;
          tmpIncidentStatData[i][robotStates.get(robot_id).name] = 0;
          tmpTaskStatData[i][robotStates.get(robot_id).name] = 0;
          tmpDistanceStatData[i][robotStates.get(robot_id).name] = 0;

          if (
            robotStates.get(robot_id).type === "Vacuum" ||
            robotStates.get(robot_id).type === "Scrubber"
          ) {
            tmpAreaCleanedStatData[i][robotStates.get(robot_id).name] = 0;
          }
        }
      }

      if ("run_times" in data) {
        for (let j = 0; j < data["run_times"].length; j++) {
          const run_time = data["run_times"][j];

          /// Ignore robots we don't wanna show
          if (
            displayedRobots.get(run_time["robot_id"]).show &&
            sameDay(daysList[i], new Date(run_time["start_time"]))
          ) {
            /// Run time graph data
            const runValue =
              run_time["user_auto_duration"] +
              run_time["user_manual_duration"] +
              run_time["operator_auto_duration"] +
              run_time["cleaning_duration"];
            tmpRunTimeStatData[i][run_time["robot_name"]] += runValue;
            tmpTotalRunTime += runValue;
            maxHours = Math.max(maxHours, Math.ceil(runValue / 3600));

            /// Idle time graph data
            tmpIdleTimeStatData[i][run_time["robot_name"]] +=
              run_time["idle_duration"];
            tmpTotalIdleTime += run_time["idle_duration"];
            maxHours = Math.max(
              maxHours,
              Math.ceil(run_time["idle_duration"] / 3600)
            );
          }
        }
      }

      /// Incident graph data
      if ("incidents" in data) {
        for (let j = 0; j < data["incidents"].length; j++) {
          const incident = data["incidents"][j];

          /// Ignore robots we don't wanna show
          if (
            displayedRobots.get(incident["robot_id"]).show &&
            sameDay(daysList[i], new Date(incident["timestamp"]))
          ) {
            tmpIncidentStatData[i][incident["robot_name"]] += 1;
            tmpTotalIncident += 1;
          }
        }
      }

      if ("tasks" in data) {
        for (let j = 0; j < data["tasks"].length; j++) {
          const task = data["tasks"][j];

          /// Ignore robots we don't wanna show
          if (
            displayedRobots.get(task["robot_id"]).show &&
            sameDay(daysList[i], new Date(task["sub_tasks"][0]["start_time"]))
          ) {
            /// Tasks performed graph data (done + cancelled)
            tmpTaskStatData[i][task["robot_name"]] += 1;
            tmpTotalTask += 1;

            for (let k = 0; k < task["sub_tasks"].length; k++) {
              const sub_task = task["sub_tasks"][k];

              /// Distance travelled graph data
              tmpDistanceStatData[i][task["robot_name"]] +=
                sub_task["distance_travelled"];
              tmpTotalDistance += sub_task["distance_travelled"];

              if (
                robotStates.get(task["robot_id"]).type === "Vacuum" ||
                robotStates.get(task["robot_id"]).type === "Scrubber"
              ) {
                /// Area cleaned graph data
                tmpAreaCleanedStatData[i][task["robot_name"]] +=
                  sub_task["area_cleaned"];
                tmpTotalAreaCleaned += sub_task["area_cleaned"];
              }
            }
          }
        }
      }
    }

    let tmpHoursTicks = [];
    for (let index = 1; index <= maxHours; index++) {
      tmpHoursTicks.push(index * 3600);
    }

    setHoursTicks(tmpHoursTicks);

    setRobotsRunTimeTotal(tmpTotalRunTime);
    setAverageRunTimeHours(tmpTotalRunTime / daysList.length / robotSet.size);
    setRunTimeStatData(tmpRunTimeStatData);

    setRobotsIdleTimeTotal(tmpTotalIdleTime);
    setAverageIdleTimeHours(tmpTotalIdleTime / daysList.length / robotSet.size);
    setIdleTimeStatData(tmpIdleTimeStatData);

    setRobotsIncidentTotal(tmpTotalIncident);
    setAverageIncident(tmpTotalIncident / daysList.length / robotSet.size);
    setIncidentStatData(tmpIncidentStatData);

    setRobotsTaskTotal(tmpTotalTask);
    setAverageTask(tmpTotalTask / daysList.length / robotSet.size);
    setTaskStatData(tmpTaskStatData);

    setRobotsDistanceTotal(tmpTotalDistance);
    setAverageDistance(tmpTotalDistance / daysList.length / robotSet.size);
    setDistanceStatData(tmpDistanceStatData);

    setRobotsAreaCleanedTotal(tmpTotalAreaCleaned);
    setAverageAreaCleaned(
      tmpTotalAreaCleaned / daysList.length / robotSet.size
    );
    setAreaCleanedStatData(tmpAreaCleanedStatData);
  }, [data, displayedRobots, fromDate, robotStates, toDate]);
  if (!data || !robotStates || robotStates.size === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          marginLeft: "1.5rem",
          marginRight: "1.5rem",
          marginBottom: "1.5rem",
          flexGrow: 1,
        }}
      >
        <Typography
          color="#c9cdd3"
          variant="h5"
          sx={{
            marginTop: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          Summary Breakdown
        </Typography>
        <Grid container spacing={"1.5rem"}>
          {/** TOTAL RUN TIME GRAPH */}
          <Grid item xs={graphXs}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Run Time -</b> {secToStr(robotsRunTimeTotal)}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <LineChart
                  width={450}
                  height={300}
                  data={runTimeStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    type="category"
                    label={{
                      position: "bottom",
                      value: get2DateStr(fromDate, toDate),
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.getDate();
                    }}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Hours",
                      style: { textAnchor: "middle" },
                      angle: -90,
                      position: "left",
                      offset: 0,
                    }}
                    tickFormatter={(value, name, props) => {
                      return Math.floor(value / 3600);
                    }}
                    ticks={hoursTicks}
                  />
                  <Tooltip
                    labelFormatter={(value, name, props) => {
                      return getDateStr(value);
                    }}
                    formatter={(value, name, props) => {
                      return secToStr(value);
                    }}
                  />
                  <ReferenceLine
                    y={averageRunTimeHours}
                    stroke="grey"
                    label={{
                      position: "insideTopLeft",
                      dy: -25,
                      value: "Average - " + secToStr(averageRunTimeHours),
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  {Array.from(displayedRobots.entries()).map((entry) => {
                    const [robot_id, robot] = entry;
                    if (robot.show) {
                      return (
                        <Line
                          key={"run-time-" + robotStates.get(robot_id).name}
                          dataKey={robotStates.get(robot_id).name}
                          stroke={robot.color}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      );
                    } else {
                      return "";
                    }
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TOTAL IDLE TIME GRAPH */}
          <Grid item xs={graphXs}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Idle Time -</b> {secToStr(robotsIdleTimeTotal)}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <LineChart
                  width={450}
                  height={300}
                  data={idleTimeStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    type="category"
                    label={{
                      position: "bottom",
                      value: get2DateStr(fromDate, toDate),
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.getDate();
                    }}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Hours",
                      style: { textAnchor: "middle" },
                      angle: -90,
                      position: "left",
                      offset: 0,
                    }}
                    tickFormatter={(value, name, props) => {
                      return Math.floor(value / 3600);
                    }}
                    ticks={hoursTicks}
                  />
                  <Tooltip
                    labelFormatter={(value, name, props) => {
                      return getDateStr(value);
                    }}
                    formatter={(value, name, props) => {
                      return secToStr(value);
                    }}
                  />
                  <ReferenceLine
                    y={averageIdleTimeHours}
                    stroke="grey"
                    label={{
                      position: "insideTopLeft",
                      dy: -25,
                      value: "Average - " + secToStr(averageIdleTimeHours),
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  {Array.from(displayedRobots.entries()).map((entry) => {
                    const [robot_id, robot] = entry;
                    if (robot.show) {
                      return (
                        <Line
                          key={"idle-time-" + robotStates.get(robot_id).name}
                          dataKey={robotStates.get(robot_id).name}
                          stroke={robot.color}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      );
                    } else {
                      return "";
                    }
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TOTAL INCIDENT GRAPH */}
          <Grid item xs={graphXs}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Number of Incidents -</b> {robotsIncidentTotal}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <LineChart
                  width={450}
                  height={300}
                  data={incidentStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    type="category"
                    label={{
                      position: "bottom",
                      value: get2DateStr(fromDate, toDate),
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.getDate();
                    }}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Incidents",
                      style: { textAnchor: "middle" },
                      angle: -90,
                      position: "left",
                      offset: 0,
                    }}
                  />
                  <Tooltip
                    labelFormatter={(value, name, props) => {
                      return getDateStr(value);
                    }}
                  />
                  <ReferenceLine
                    y={averageIncident}
                    stroke="grey"
                    label={{
                      position: "insideTopLeft",
                      dy: -25,
                      value:
                        "Average - " +
                        averageIncident.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        }),
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  {Array.from(displayedRobots.entries()).map((entry) => {
                    const [robot_id, robot] = entry;
                    if (robot.show) {
                      return (
                        <Line
                          key={"incident-" + robotStates.get(robot_id).name}
                          dataKey={robotStates.get(robot_id).name}
                          stroke={robot.color}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      );
                    } else {
                      return "";
                    }
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TOTAL TASK GRAPH */}
          <Grid item xs={graphXs}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Number of Tasks Performed -</b> {robotsTaskTotal}
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <LineChart
                  width={450}
                  height={300}
                  data={taskStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    type="category"
                    label={{
                      position: "bottom",
                      value: get2DateStr(fromDate, toDate),
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.getDate();
                    }}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Tasks",
                      style: { textAnchor: "middle" },
                      angle: -90,
                      position: "left",
                      offset: 0,
                    }}
                  />
                  <Tooltip
                    labelFormatter={(value, name, props) => {
                      return getDateStr(value);
                    }}
                  />
                  <ReferenceLine
                    y={averageTask}
                    stroke="grey"
                    label={{
                      position: "insideTopLeft",
                      dy: -25,
                      value:
                        "Average - " +
                        averageTask.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        }),
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  {Array.from(displayedRobots.entries()).map((entry) => {
                    const [robot_id, robot] = entry;
                    if (robot.show) {
                      return (
                        <Line
                          key={"task-" + robotStates.get(robot_id).name}
                          dataKey={robotStates.get(robot_id).name}
                          stroke={robot.color}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      );
                    } else {
                      return "";
                    }
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TOTAL DISTANCE GRAPH */}
          <Grid item xs={graphXs}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Distance Travelled -</b>{" "}
                {robotsDistanceTotal.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
                m
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <LineChart
                  width={450}
                  height={300}
                  data={distanceStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    type="category"
                    label={{
                      position: "bottom",
                      value: get2DateStr(fromDate, toDate),
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.getDate();
                    }}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Distances (in meters)",
                      style: { textAnchor: "middle" },
                      angle: -90,
                      position: "left",
                      offset: 0,
                    }}
                  />
                  <Tooltip
                    labelFormatter={(value, name, props) => {
                      return getDateStr(value);
                    }}
                    formatter={(value, name, props) => {
                      return (
                        value.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) + "m"
                      );
                    }}
                  />
                  <ReferenceLine
                    y={averageDistance}
                    stroke="grey"
                    label={{
                      position: "insideTopLeft",
                      dy: -25,
                      value:
                        "Average - " +
                        averageDistance.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) +
                        "m",
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  {Array.from(displayedRobots.entries()).map((entry) => {
                    const [robot_id, robot] = entry;
                    if (robot.show) {
                      return (
                        <Line
                          key={"distance-" + robotStates.get(robot_id).name}
                          dataKey={robotStates.get(robot_id).name}
                          stroke={robot.color}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      );
                    } else {
                      return "";
                    }
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/** TOTAL AREA CLEANED GRAPH */}
          <Grid item xs={graphXs}>
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                minHeight: graphHeight,
                backgroundColor: "white",
                borderRadius: "2px",
                padding: "1rem",
              }}
            >
              <Typography
                color="black"
                variant="h5"
                sx={{
                  paddingBottom: "1rem",
                }}
              >
                <b>Total Area Cleaned -</b>{" "}
                {robotsAreaCleanedTotal.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
                m<sup>2</sup>
              </Typography>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minHeight={graphHeight - 80}
              >
                <LineChart
                  width={450}
                  height={300}
                  data={areaCleanedStatData}
                  margin={{
                    top: 24,
                    right: 70,
                    left: 24,
                    bottom: 24,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    type="category"
                    label={{
                      position: "bottom",
                      value: get2DateStr(fromDate, toDate),
                    }}
                    tickFormatter={(value, name, props) => {
                      return value.getDate();
                    }}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "Area cleaned (in square meters)",
                      style: { textAnchor: "middle" },
                      angle: -90,
                      position: "left",
                      offset: 0,
                    }}
                  />
                  <Tooltip
                    labelFormatter={(value, name, props) => {
                      return getDateStr(value);
                    }}
                    formatter={(value, name, props) => {
                      return (
                        value.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) + "m²"
                      );
                    }}
                  />
                  <ReferenceLine
                    y={averageAreaCleaned}
                    stroke="grey"
                    label={{
                      position: "insideTopLeft",
                      dy: -25,
                      value:
                        "Average - " +
                        averageAreaCleaned.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        }) +
                        "m²",
                    }}
                    strokeDasharray="6 6"
                    isFront={true}
                  />
                  {Array.from(displayedRobots.entries()).map((entry) => {
                    const [robot_id, robot] = entry;
                    if (robot.show) {
                      return (
                        <Line
                          key={"area-cleaned-" + robotStates.get(robot_id).name}
                          dataKey={robotStates.get(robot_id).name}
                          stroke={robot.color}
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        />
                      );
                    } else {
                      return "";
                    }
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
}

export default MultiDayGraphList;
